const MAX_ATTEMPTS = 4
const RETRY_BASE_DELAY_MS = 500

const GLB_HEADER_BYTES = 12
const GLB_MAGIC = 0x46546c67

function delay(ms: number) {
    return new Promise<void>((resolve) => {
        window.setTimeout(resolve, ms)
    })
}

async function fetchAssetBytes(url: string, revalidate: boolean): Promise<ArrayBuffer> {
    const response = await fetch(url, {
        cache: revalidate ? 'reload' : 'default',
    })

    if (!response.ok) {
        throw new Error(`${url} responded with ${response.status} ${response.statusText}`)
    }

    // no extra check because the content-length is already checked in the fetch call
    return await response.arrayBuffer()
}

export async function withRetry<T>(
    label: string,
    run: (revalidate: boolean) => Promise<T>,
): Promise<T> {
    let lastError: unknown

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
        try {
            return await run(attempt > 1)
        } catch (error) {
            lastError = error
            console.warn(
                `[assetFetcher] attempt ${attempt}/${MAX_ATTEMPTS} for ${label} failed`,
                error,
            )
            if (attempt < MAX_ATTEMPTS) {
                await delay(RETRY_BASE_DELAY_MS * attempt)
            }
        }
    }

    throw lastError instanceof Error ? lastError : new Error(`Could not load ${label}`)
}

export async function loadAssetWithRetry<T>(
    url: string,
    decode: (bytes: ArrayBuffer) => T | Promise<T>,
): Promise<T> {
    return withRetry(url, async (revalidate) => decode(await fetchAssetBytes(url, revalidate)))
}

export function decodeAssetJson<T>(bytes: ArrayBuffer): T {
    const text = new TextDecoder().decode(bytes)
    if (text.trimStart().startsWith('<')) {
        throw new Error('received HTML instead of JSON')
    }
    return JSON.parse(text) as T
}

export function assertCompleteGlb(url: string, bytes: ArrayBuffer): void {
    if (bytes.byteLength < GLB_HEADER_BYTES) {
        throw new Error(`${url} is too short to be a GLB file`)
    }

    const header = new DataView(bytes, 0, GLB_HEADER_BYTES)
    if (header.getUint32(0, true) !== GLB_MAGIC) {
        throw new Error(`${url} is not a GLB file`)
    }

    const declaredByteLength = header.getUint32(8, true)
    if (declaredByteLength !== bytes.byteLength) {
        throw new Error(
            `${url} is incomplete, got ${bytes.byteLength} of ${declaredByteLength} bytes`,
        )
    }
}
